// ─────────────────────────────────────────────────────────────────────────────
// patternsSlice.ts — Redux slice for design pattern playground data
//
// Stores user-entered playground inputs. Entries are persisted in localStorage
// via redux-persist so users can save test data between sessions.
// ─────────────────────────────────────────────────────────────────────────────

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PatternPlaygroundEntry, PatternId } from '@/types/cv.types';

interface PatternsState {
  /** All playground entries, keyed by their generated id */
  entries: PatternPlaygroundEntry[];
}

const initialState: PatternsState = {
  entries: [],
};

const patternsSlice = createSlice({
  name: 'patterns',
  initialState,

  reducers: {
    /**
     * addEntry — Saves a new playground submission.
     * The ID is generated by the component so it can be an optimistic update.
     */
    addEntry: (state, action: PayloadAction<PatternPlaygroundEntry>) => {
      state.entries.unshift(action.payload); // Most recent first
    },

    /**
     * removeEntry — Deletes a saved entry by ID
     */
    removeEntry: (state, action: PayloadAction<string>) => {
      state.entries = state.entries.filter((e) => e.id !== action.payload);
    },

    /**
     * clearPatternEntries — Wipes all entries for a specific pattern.
     * Useful for "Reset demo" functionality.
     */
    clearPatternEntries: (state, action: PayloadAction<PatternId>) => {
      state.entries = state.entries.filter(
        (e) => e.patternId !== action.payload
      );
    },

    /** clearAllEntries — Nuclear option, wipes the entire playground store */
    clearAllEntries: (state) => {
      state.entries = [];
    },
  },
});

export const {
  addEntry,
  removeEntry,
  clearPatternEntries,
  clearAllEntries,
} = patternsSlice.actions;

export default patternsSlice.reducer;
